/**
 * BuriedVerse e05_hell "Hell" — FULL overlay composition.
 * Generated by bv_overlay_gen.py. fps-SAFE (S(sec) reads useVideoConfig().fps) so the SAME comp is:
 *   • buriedverse-e05-overlays      @16fps → the 720p 0.5x preview (baked logo)
 *   • buriedverse-e05-overlays-4k   @24fps → the Topaz 4K base, logo via ffmpeg (showLogo=false)
 * Devotional route (== bible_decoder W01): overlay_design_spec route=devotional; build_overlay_windows.py
 * EXTRACTS every `start: X, out: Y` NUMERIC LITERAL below → factory_4k_full renders those clusters @scale=3.
 * Anchors are REAL times from productions.word_timestamps_hindi. 38 events (11 BvFactTag + 27 BvHero).
 */
import {
  AbsoluteFill, Img, Sequence, staticFile, useCurrentFrame, useVideoConfig, interpolate,
} from "remotion";
import { BvFactTag, BvHero } from "./BuriedVerseOverlayDemo";

export const e05_hell_FPS = 16;
export const e05_hell_DURATION_FRAMES = 26067;
export const e05_hell_FPS_4K = 24;
export const e05_hell_DURATION_FRAMES_4K = 39101;

const BvLogo: React.FC = () => {
  const frame = useCurrentFrame();
  const fadeIn = interpolate(frame, [0, 36], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <Img src={staticFile("buried_verse_diorama_ko.png")}
      style={{ position: "absolute", top: 20, right: 20, width: 70, height: "auto", opacity: fadeIn * 0.85 }} />
  );
};

const FadeOut: React.FC<{ dur: number; children: React.ReactNode }> = ({ dur, children }) => {
  const frame = useCurrentFrame();
  const o = interpolate(frame, [dur - 14, dur], [1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return <AbsoluteFill style={{ opacity: o }}>{children}</AbsoluteFill>;
};

type Tag = { start: number; out: number; kicker: string; value: string };
type Hero = {
  start: number; out: number; variant: "word" | "myth" | "quote"; kicker: string;
  word?: string; translit?: string; gloss?: string; headline?: string; attribution?: string;
};

// ── SLOT 1 · fact tags (BvFactTag: citation / place / author) ──
const TAGS: Tag[] = [
  // King James Bible, that single word "hell" shows up around fifty-four times, an
  { start: 206.832, out: 211.432, kicker: "Citation", value: "King James · “hell” about 54 times" },
  // and tell him his favorite son Joseph has been torn apart by an animal.
  { start: 327.119, out: 331.719, kicker: "Citation", value: "Genesis 37 · Jacob mourns Joseph" },
  // Job it is a land of gloom and deep shadow, dark as darkness
  { start: 376.003, out: 380.603, kicker: "Citation", value: "Job 10 · a land of gloom and deep shadow" },
  // sixteenth Psalm says, you will not abandon my soul to Sheol, it is not
  { start: 450.268, out: 454.868, kicker: "Citation", value: "Psalm 16 · “you will not abandon my soul to Sheol”" },
  // 32:22, says God's fire burns down to the depths of Sheol. But the sorted,
  { start: 485.957, out: 490.557, kicker: "The caveat", value: "Deuteronomy 32 · fire burns to the depths of Sheol" },
  // book of Revelation, death and Hades are themselves thrown into the lake of fir
  { start: 550.902, out: 555.502, kicker: "Citation", value: "Revelation 20 · death and Hades thrown into the lake of fire" },
  // an inner geography, the rich man and Lazarus, where a rich
  { start: 594.237, out: 598.837, kicker: "Citation", value: "Luke 16 · the rich man and Lazarus" },
  // the commentary of a medieval French rabbi named David Kimhi, known as Radak,
  { start: 861.892, out: 866.492, kicker: "Author", value: "David Kimhi (Radak) · c. 1200 AD" },
  // calls Molech, at a specific site within the valley called Topheth. Ahaz did it
  { start: 1002.61, out: 1007.21, kicker: "Place", value: "Topheth · inside the Valley of Hinnom" },
  // such a thing, that it had never even entered his mind, and that because
  { start: 1033.734, out: 1038.334, kicker: "Citation", value: "Jeremiah 7 · “it never entered my mind”" },
  // Kings tells us, was to defile this valley, Topheth itself, to
  { start: 1100.681, out: 1105.281, kicker: "Citation", value: "2 Kings 23 · Josiah defiles Topheth" },
];

// ── SLOT 2 · hero cards (BvHero: word / myth / quote) ──
const HEROES: Hero[] = [
  // nobody tells you. That correction is also a myth. The burning-garbage-dump sto
  { start: 44.162, out: 51.162, variant: "myth", kicker: "The picture", headline: "The cartoon was wrong. Then the fix you were handed turned out to have a hole in it too." },
  // English Bible hid four different source words under one small syllable, that t
  { start: 92.089, out: 99.089, variant: "myth", kicker: "The four words", headline: "Your English Bible hid four different source words under one small syllable." },
  // which was both the name of the shadowy underworld of their pagan
  { start: 132.822, out: 140.022, variant: "word", kicker: "The word", word: "Hel", translit: "hel", gloss: "Norse. The hidden realm where the dead go down, and the goddess who ruled it." },
  // beings at all. Four distinct pictures, four different locations, four differen
  { start: 187.443, out: 194.443, variant: "myth", kicker: "The flattening", headline: "Four distinct pictures, four locations, one pagan-rooted English word poured over all of them." },
  // the oldest, is the Hebrew word Sheol, and it appears
  { start: 298.604, out: 305.804, variant: "word", kicker: "The word", word: "שְׁאוֹל", translit: "Sheol", gloss: "The silent grave. Where everybody goes, righteous and wicked alike." },
  // over. He says, I will go down to Sheol to my son, mourning. Sit with
  { start: 336.581, out: 343.781, variant: "quote", kicker: "The grave", headline: "“I will go down to Sheol to my son, mourning.”", attribution: "Jacob · Genesis 37" },
  // is Sheol. Because Sheol is not a punishment. It is simply
  { start: 353.198, out: 360.198, variant: "myth", kicker: "The leveller", headline: "Sheol is not a punishment. It is the common house of all the dead." },
  // Sheol, the Rephaim, usually rendered "the shades, " pictured as
  { start: 396.858, out: 404.058, variant: "word", kicker: "The word", word: "רְפָאִים", translit: "Rephaim", gloss: "The shades. Faint, weakened echoes of the living, gathered in the dust." },
  // fire." They say, you too have become as weak as we are. That is Sheol.
  { start: 426.618, out: 433.818, variant: "quote", kicker: "The greeting", headline: "“You too have become as weak as we are.”", attribution: "Isaiah 14 · the shades to the tyrant" },
  // centuries before Jesus, the usual Greek word chosen for Sheol was Hades, lifte
  { start: 513.053, out: 520.253, variant: "word", kicker: "The word", word: "ᾅδης", translit: "Hades", gloss: "The realm of the dead. A waiting place, not the final fire." },
  // Read that slowly. Hades gets thrown into the fire. Which means Hades
  { start: 556.707, out: 563.707, variant: "myth", kicker: "The proof", headline: "Hades gets thrown into the fire. So Hades cannot itself be the final fire." },
  // whatever it is, it is set in Hades, the interim realm,
  { start: 621.088, out: 628.088, variant: "myth", kicker: "The waiting", headline: "Whatever that scene is, it is set in Hades, the interim realm. It is not the last word." },
  // taught in, but it arrived late, it grew over time, and it was
  { start: 742.098, out: 749.098, variant: "myth", kicker: "The development", headline: "The fire is really there in the world Jesus taught in. It arrived late, and it grew." },
  // the Hebrew Gei-Hinnom, the Valley of Hinnom, a real ravine along the southern
  { start: 792.115, out: 799.315, variant: "word", kicker: "The word", word: "γέεννα", translit: "Gehenna", gloss: "The Valley of Hinnom. A real ravine along the edge of old Jerusalem." },
  // goes like this: Gehenna was Jerusalem's garbage dump, a place where
  { start: 805.467, out: 812.467, variant: "myth", kicker: "The insider answer", headline: "Gehenna was Jerusalem's burning garbage dump. Tidy, deflating, and unsupported." },
  // law and tradition. They could not find it in Josephus, the first-century histo
  { start: 844.99, out: 851.99, variant: "myth", kicker: "The search", headline: "Not in the Bible. Not in the Mishnah. Not in Josephus. Not in any ancient source." },
  // other words, like an educated medieval guess that sounded plausible, got copie
  { start: 888.738, out: 895.738, variant: "myth", kicker: "The birth certificate", headline: "An educated medieval guess that sounded plausible, got copied, and went viral." },
  // his descendant Manasseh, this valley became the place of child sacrifice. The 
  { start: 987.808, out: 994.808, variant: "myth", kicker: "The real history", headline: "Two kings of Judah burned their own children in this valley." },
  // Hinnom, Jeremiah said, but the Valley of Slaughter, and it would
  { start: 1046.312, out: 1053.512, variant: "quote", kicker: "The renaming", headline: "“No longer Topheth, but the Valley of Slaughter.”", attribution: "Jeremiah 7" },
  // all of them, smash the jar on the ground, shattering it so
  { start: 1067.988, out: 1074.988, variant: "myth", kicker: "The jar", headline: "A clay jar smashed at the valley gate. Broken so completely it could never be mended." },
  // is the picture. The horror of Gehenna was never garbage. It was memory.
  { start: 1158.831, out: 1165.831, variant: "myth", kicker: "The verdict", headline: "The horror of Gehenna was never garbage. It was memory." },
  // uses there is built on the word Tartarus. In Greek mythology,
  { start: 1184.55, out: 1191.75, variant: "word", kicker: "The word", word: "ταρταρόω", translit: "tartaroo", gloss: "Built on Tartarus. A prison for rebel angels, not a destination for the human dead." },
  // duration is contested. The Greek word translated "eternal" in the most-quoted
  { start: 1262.169, out: 1269.369, variant: "word", kicker: "The word", word: "αἰώνιος", translit: "aionios", gloss: "Related to the noun for an age or an era. The duration is genuinely contested." },
  // serious, scripture-loving Christians have held three different readings of fin
  { start: 1304.875, out: 1311.875, variant: "myth", kicker: "The three readings", headline: "Unending torment. Final destruction. A judgment that restores. All held by serious Christians." },
  // as simple fact is a cultural inheritance, not a plain
  { start: 1346.265, out: 1353.265, variant: "myth", kicker: "The inheritance", headline: "The no-debate-exists version you were handed is a cultural inheritance, not a plain reading." },
  // and Jesus answers, today you will be with me in paradise. Notice the word.
  { start: 1421.54, out: 1428.74, variant: "quote", kicker: "The other word", headline: "“Today you will be with me in paradise.” Not Sheol. Not Hades. Not Gehenna.", attribution: "Luke 23" },
  // alone carries it. Gehenna is that kind of name. Jesus did not
  { start: 1497.011, out: 1504.011, variant: "myth", kicker: "The valley", headline: "Gehenna is a name that stopped being geography and became a verdict." },
];

export const BuriedVerseE05HellOverlays: React.FC<{ showLogo?: boolean }> = ({ showLogo = true }) => {
  const { fps } = useVideoConfig();
  const S = (sec: number) => Math.round(sec * fps);
  return (
    <AbsoluteFill>
      {showLogo && <BvLogo />}
      {TAGS.map((t, i) => (
        <Sequence key={`tag-${i}`} from={S(t.start)} durationInFrames={S(t.out - t.start)}>
          <FadeOut dur={S(t.out - t.start)}>
            <BvFactTag kicker={t.kicker} value={t.value} />
          </FadeOut>
        </Sequence>
      ))}
      {HEROES.map((h, i) => (
        <Sequence key={`hero-${i}`} from={S(h.start)} durationInFrames={S(h.out - h.start)}>
          <FadeOut dur={S(h.out - h.start)}>
            <BvHero variant={h.variant} kicker={h.kicker} word={h.word} translit={h.translit}
              gloss={h.gloss} headline={h.headline} attribution={h.attribution} />
          </FadeOut>
        </Sequence>
      ))}
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
